import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Users, DollarSign, Building2, TrendingUp, BarChart3, PieChart as PieChartIcon, FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useCommissionSummary } from "@/hooks/useCommissionSummary";
import { formatGrowthPct, isActiveOn, type RecordShare } from "@shared/utils/commissionSummary";

// Helper functions
const parseCommissionValue = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[$,]/g, '')) || 0;
};

const formatCurrency = (value: string | number) => {
  if (!value) return '-';
  const numericValue = typeof value === 'number' ? value : parseFloat(String(value).replace(/[$,]/g, ''));
  if (isNaN(numericValue)) return '-';
  return `$${numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const formatMonth = (monthString: string): string => {
  try {
    const [year, month] = monthString.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return 'N/A';
  }
};

export default function AgentCommissions() {
  const { toast } = useToast();
  const { canAny } = usePermissions();

  const canGenerateAgentStatements = canAny([
    "commissions:reports",
    // Backward compatibility / admins who may be viewing via employee portal
    "commissions:manage",
  ]);

  const tabsListClass = canGenerateAgentStatements
    ? "grid w-full grid-cols-4"
    : "grid w-full grid-cols-3";
  // Get current user
  const { data: user } = useQuery({ queryKey: ["/api/auth/user"] });
  
  // Get current employee record - AgencyRepresentatives stores employee.id as userId in clientAccountManagers
  const { data: employeeRecord } = useQuery({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await fetch("/api/employees/me", { credentials: "include" });
      if (!response.ok) return null;
      return response.json();
    },
  });

  // Account manager assignments are keyed by `client_account_managers.user_id` which
  // is intended to reference `users.id` (UUID). Some older data may have stored
  // `employees.id` as a string. Support both.
  const currentAccountManagerUserIds = useMemo(() => {
    const ids = new Set<string>();
    const userId = (user as any)?.id;
    if (userId) ids.add(String(userId));
    if (employeeRecord?.id !== undefined && employeeRecord?.id !== null) {
      ids.add(String(employeeRecord.id));
    }
    return ids;
  }, [user, employeeRecord]);

  const currentAccountManagerUserId = useMemo(() => {
    const userId = (user as any)?.id;
    if (userId) return String(userId);
    if (employeeRecord?.id !== undefined && employeeRecord?.id !== null) {
      return String(employeeRecord.id);
    }
    return "";
  }, [user, employeeRecord]);

  const currentEmployeeIdForStatements = useMemo(() => {
    if (employeeRecord?.id !== undefined && employeeRecord?.id !== null) {
      return String(employeeRecord.id);
    }
    return "";
  }, [employeeRecord]);

  // Commission filters
  const [agentCommissionClientFilter, setAgentCommissionClientFilter] = useState("all");
  const [agentCommissionCarrierFilter, setAgentCommissionCarrierFilter] = useState("all");
  const [agentCommissionMonthFilter, setAgentCommissionMonthFilter] = useState("all");
  const [generatingStatementMonth, setGeneratingStatementMonth] = useState<string | null>(null);
  const [statementFilterMonth, setStatementFilterMonth] = useState<string>("all");

  // Fetch split profiles
  const { data: agencyAgentSplitProfiles = [] } = useQuery({
    queryKey: ["/api/commission-split-profiles", "agency_agent"],
    queryFn: async () => {
      const response = await fetch("/api/commission-split-profiles?splitType=agency_agent", { credentials: "include" });
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: primarySecondarySplitProfiles = [] } = useQuery({
    queryKey: ["/api/commission-split-profiles", "primary_secondary"],
    queryFn: async () => {
      const response = await fetch("/api/commission-split-profiles?splitType=primary_secondary", { credentials: "include" });
      if (!response.ok) return [];
      return response.json();
    },
  });


  // Fetch commissions first to get client IDs
  const { data: commissions = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions"],
    queryFn: async () => {
      const res = await fetch("/api/commissions", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    }
  });

  const clientAgencyAgentSplitIdByClientId = useMemo(() => {
    const map = new Map<number, number>();
    commissions.forEach((record: any) => {
      const clientId = Number(record.clientId);
      if (!Number.isFinite(clientId)) return;
      const raw = (record as any).clientAgencyAgentSplitId;
      if (raw === null || raw === undefined || raw === "") return;
      const splitId = Number(raw);
      if (!Number.isFinite(splitId)) return;
      if (!map.has(clientId)) map.set(clientId, splitId);
    });
    return map;
  }, [commissions]);

  // Minimal client metadata for commission calculations (includes client-level agency/agent split).
  const { data: commissionClients = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/clients", (user as any)?.id ?? null],
    queryFn: async () => {
      const res = await fetch("/api/commissions/clients", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!(user as any)?.id,
  });

  // Get unique client IDs from commissions
  const clientIdsFromCommissions = useMemo(() => {
    const ids = new Set<number>();
    commissions.forEach((comm: any) => {
      if (comm.clientId) {
        ids.add(Number(comm.clientId));
      }
    });
    return Array.from(ids);
  }, [commissions]);

  // Fetch carriers
  const { data: carriers = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/carriers"],
    queryFn: async () => {
      // Avoid requiring `carriers:view` in the employee portal.
      const res = await fetch("/api/commissions/carriers", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch employees
  const { data: employees = [] } = useQuery({
    queryKey: ["/api/employees", "agency"],
    queryFn: () => fetch("/api/employees", { credentials: "include" }).then(res => res.json()).then(data => 
      data.filter((emp: any) => !emp.clientId && !emp.prospectId)
    ),
  });

  // Fetch client account managers for current employee (my assignments)
  const { data: myAccountManagers = [] } = useQuery<any[]>({
    queryKey: ["/api/account-managers/me", currentAccountManagerUserId],
    queryFn: async () => {
      try {
        const res = await fetch("/api/account-managers/me", { credentials: "include" });
        if (res.ok) {
          return await res.json();
        }
        return [];
      } catch (error) {
        console.error("Error fetching account managers:", error);
        return [];
      }
    },
    enabled: !!currentAccountManagerUserId,
  });

  // Get client IDs from my account manager assignments (in case we don't have commissions yet)
  const clientIdsFromAssignments: number[] = useMemo(() => {
    const ids = new Set<number>();
    myAccountManagers.forEach((m: any) => {
      if (m.clientId) {
        ids.add(Number(m.clientId));
      }
    });
    return Array.from(ids);
  }, [myAccountManagers]);

  // Combine client IDs from both sources
  const allClientIds: number[] = useMemo(() => {
    const ids = new Set<number>();
    clientIdsFromCommissions.forEach((id: number) => ids.add(id));
    clientIdsFromAssignments.forEach((id: number) => ids.add(id));
    return Array.from(ids);
  }, [clientIdsFromCommissions, clientIdsFromAssignments]);

  // Fetch all client account managers for these clients (needed to detect multiple agents and primary/secondary roles)
  const { data: allClientAccountManagers = [] } = useQuery<any[]>({
    queryKey: ["/api/client-account-managers", allClientIds.join(",")],
    queryFn: async () => {
      // Parallel, not one client at a time (DEV-328)
      const perClient = await Promise.all(
        allClientIds.map(async (clientId) => {
          try {
            const res = await fetch(`/api/clients/${clientId}/account-managers`, { credentials: "include" });
            return res.ok ? await res.json() : [];
          } catch (error) {
            console.error(`Error fetching account managers for client ${clientId}:`, error);
            return [];
          }
        })
      );
      return perClient.flat();
    },
    enabled: allClientIds.length > 0,
  });

  // Skip fetching clients - commissions already include clientName from getCommissionsWithDetails()
  const clients: any[] = [];

  // Create a client name map - prioritize commissions (they already have clientName from DB join)
  const clientNameMap = useMemo(() => {
    const map = new Map<number, string>();
    
    // First, add client names from commissions (primary source - already has clientName from DB)
    commissions.forEach((comm: any) => {
      if (comm.clientId && comm.clientName) {
        map.set(Number(comm.clientId), comm.clientName);
      }
    });
    
    // Then, add client names from my account managers as fallback (endpoint includes it)
    myAccountManagers.forEach((m: any) => {
      if (m.clientId && m.clientName && !map.has(Number(m.clientId))) {
        map.set(Number(m.clientId), m.clientName);
      }
    });
    
    return map;
  }, [commissions, myAccountManagers]);

  // DEV-328: totals, per-record shares and growth come from the server summary
  // (the same one Agency > Commissions uses), filtered to this agent's splits.
  const localToday = getLocalDateString();
  const summaryMonth = agentCommissionMonthFilter !== "all" ? agentCommissionMonthFilter : localToday.slice(0, 7);
  const { data: commissionSummary } = useCommissionSummary("me", summaryMonth, localToday);
  const shareByRecordId = useMemo(() => {
    const map = new Map<number, RecordShare>();
    (commissionSummary?.shares ?? []).forEach((s) => map.set(s.recordId, s));
    return map;
  }, [commissionSummary]);

  // Current producer assignments — what "Assigned Clients" counts (ended and
  // non-producer rows used to inflate it).
  // Note: AgencyRepresentatives stores employee.id (as string) in clientAccountManagers.userId
  const myClientAssignments = useMemo(() => {
    if (!currentAccountManagerUserId) return [];
    return myAccountManagers.filter(
      (m: any) =>
        currentAccountManagerUserIds.has(String(m.userId)) &&
        String(m.role || "").toLowerCase().includes("producer") &&
        isActiveOn(m, localToday)
    );
  }, [myAccountManagers, currentAccountManagerUserId, currentAccountManagerUserIds, localToday]);

  // Clients with a current assignment or commission shares (for the filters)
  const myClientIds = useMemo(() => {
    const ids = new Set<number>();
    myClientAssignments.forEach((m: any) => {
      const id = Number(m.clientId);
      if (!isNaN(id)) ids.add(id);
    });
    (commissionSummary?.shares ?? []).forEach((s) => ids.add(s.clientId));
    return ids;
  }, [myClientAssignments, commissionSummary]);

  // Agency/Agent split is client-scoped.
  const getClientAgencySplit = (clientId: number) => {
    const embeddedSplitId = clientAgencyAgentSplitIdByClientId.get(Number(clientId));

    const clientMeta = (commissionClients as any[]).find(
      (c: any) => Number(c.id) === Number(clientId)
    );
    const rawAssignedSplitId = clientMeta?.agencyAgentSplitId;
    const assignedSplitId =
      rawAssignedSplitId === null || rawAssignedSplitId === undefined || rawAssignedSplitId === ""
        ? null
        : Number(rawAssignedSplitId);

    const resolvedSplitId =
      embeddedSplitId !== undefined
        ? embeddedSplitId
        : assignedSplitId !== null && Number.isFinite(assignedSplitId)
          ? assignedSplitId
          : null;

    const splitProfile =
      (resolvedSplitId !== null
        ? agencyAgentSplitProfiles.find((p: any) => Number(p.id) === Number(resolvedSplitId))
        : undefined) ||
      agencyAgentSplitProfiles.find((p: any) => p.isDefault);

    if (!splitProfile) {
      return { agentPercentage: "50%", agencyPercentage: "50%", splitName: "Standard Split" };
    }

    return {
      agentPercentage: `${splitProfile.agentPercentage}%`,
      agencyPercentage: `${splitProfile.agencyPercentage}%`,
      splitName: splitProfile.name,
    };
  };

  // Get client agent split (same pattern as CommissionManagementTab)
  const getClientAgentSplit = (clientId: number) => {
    const myAssignment = myClientAssignments.find((m: any) => Number(m.clientId) === Number(clientId));
    const assignedSplitId = myAssignment?.primarySecondarySplitId;
    const splitProfile =
                        primarySecondarySplitProfiles.find((p: any) => Number(p.id) === Number(assignedSplitId)) || 
                        primarySecondarySplitProfiles.find((p: any) => p.isDefault);
    
    if (!splitProfile) {
      return { primaryPercentage: 50, secondaryPercentage: 50, splitName: "Equal Split" };
    }
    
    return {
      primaryPercentage: splitProfile.primaryPercentage,
      secondaryPercentage: splitProfile.secondaryPercentage,
      splitName: splitProfile.name
    };
  };

  // Get my commission data
  const getMyCommissionData = () => {
    // Ensure clientId is a number for comparison
    let records = commissions.filter((record: any) => shareByRecordId.has(Number(record.id)));

    // Apply filters
    if (agentCommissionClientFilter !== "all") {
      records = records.filter((r: any) => r.clientId?.toString() === agentCommissionClientFilter);
    }
    if (agentCommissionCarrierFilter !== "all") {
      records = records.filter((r: any) => r.carrierId?.toString() === agentCommissionCarrierFilter);
    }
    if (agentCommissionMonthFilter !== "all") {
      records = records.filter((r: any) => (r.accountingMonth || r.month) === agentCommissionMonthFilter);
    }

    return records;
  };

  // Available months based on actual commission data (for this agent's clients).
  // Respect the currently-selected client/carrier filters so month choices stay relevant.
  const availableMonths = useMemo(() => {
    const months = new Set<string>();

    commissions.forEach((record: any) => {
      if (!shareByRecordId.has(Number(record.id))) return;

      if (agentCommissionClientFilter !== "all" && String(record.clientId) !== agentCommissionClientFilter) return;
      if (agentCommissionCarrierFilter !== "all" && String(record.carrierId) !== agentCommissionCarrierFilter) return;

      const month = String(record.accountingMonth || record.month || "");
      if (!/^\d{4}-\d{2}$/.test(month)) return;
      months.add(month);
    });

    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [commissions, shareByRecordId, agentCommissionClientFilter, agentCommissionCarrierFilter]);

  // If the currently selected month becomes unavailable (e.g., after switching filters), reset gracefully.
  useEffect(() => {
    if (agentCommissionMonthFilter === "all") return;
    if (!availableMonths.includes(agentCommissionMonthFilter)) {
      setAgentCommissionMonthFilter("all");
    }
  }, [agentCommissionMonthFilter, availableMonths]);

  // Analytics computations (similar pattern as CommissionManagementTab)
  const analyticsData = useMemo(() => {
    const getAgentAmountForRecord = (record: any) => shareByRecordId.get(Number(record.id))?.share ?? 0;

    // Records used for analytics breakdown respect current filters
    const myRecords = getMyCommissionData();

    const carrierTotals: Record<string, { total: number; count: number }> = {};
    const coverageTotals: Record<string, number> = {};
    const monthlyTotals: Record<string, number> = {};

    myRecords.forEach((record: any) => {
      const agentAmount = getAgentAmountForRecord(record);
      const carrierName = record.carrierName || 'Unknown';
      const coverageType = (record.planType || 'Unknown').replace(' Insurance', '');
      const month = record.accountingMonth || record.month;

      if (!carrierTotals[carrierName]) carrierTotals[carrierName] = { total: 0, count: 0 };
      carrierTotals[carrierName].total += agentAmount;
      carrierTotals[carrierName].count += 1;
      coverageTotals[coverageType] = (coverageTotals[coverageType] || 0) + agentAmount;
      if (month) monthlyTotals[month] = (monthlyTotals[month] || 0) + agentAmount;
    });

    const topCarrier = Object.entries(carrierTotals).sort((a, b) => b[1].total - a[1].total)[0]?.[0] || "N/A";
    const topCoverage = Object.entries(coverageTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A";

    const sortedMonths = Object.keys(monthlyTotals).sort();
    const monthlyTrendData = sortedMonths.map((month, index) => {
      const commission = monthlyTotals[month];
      const prevCommission = index > 0 ? monthlyTotals[sortedMonths[index - 1]] : commission;
      const variance = prevCommission > 0 ? ((commission - prevCommission) / prevCommission) * 100 : 0;
      const [year, monthNum] = month.split('-').map(Number);
      const date = new Date(year, monthNum - 1, 1);
      return { month: date.toLocaleDateString('en-US', { month: 'short' }), commissions: Math.round(commission), variance: parseFloat(variance.toFixed(1)) };
    });

    const avgCarrierTotal = Object.values(carrierTotals).reduce((sum, d) => sum + d.total, 0) / (Object.keys(carrierTotals).length || 1);
    const carrierData = Object.entries(carrierTotals)
      .map(([carrier, data]) => ({
        carrier: carrier.replace(' Blue Cross', '').replace(' Dental', ''),
        amount: Math.round(data.total),
        variance: parseFloat((avgCarrierTotal > 0 ? ((data.total - avgCarrierTotal) / avgCarrierTotal) * 100 : 0).toFixed(1))
      }))
      .sort((a, b) => b.amount - a.amount).slice(0, 6);

    const totalCoverage = Object.values(coverageTotals).reduce((sum, v) => sum + v, 0);
    const coverageTypeData = Object.entries(coverageTotals)
      .map(([name, value]) => ({ name, value: Math.round(value), percentage: totalCoverage > 0 ? Math.round((value / totalCoverage) * 100) : 0 }))
      .sort((a, b) => b.value - a.value);

    return {
      // Server summary: same numbers Agency > Commissions shows for this agent
      totalYTD: commissionSummary?.totalCommissions ?? 0,
      thisMonthTotal: commissionSummary?.monthTotal ?? 0,
      avgGrowth: commissionSummary?.avgMonthlyGrowthPct ?? null,
      topCarrier,
      topCarrierAmount: Math.round(carrierTotals[topCarrier]?.total || 0),
      topCoverage,
      topCoveragePercent: coverageTypeData[0]?.percentage || 0,
      monthlyTrendData,
      carrierData,
      coverageTypeData
    };
  }, [
    commissions,
    commissionSummary,
    shareByRecordId,
    agentCommissionClientFilter,
    agentCommissionCarrierFilter,
    agentCommissionMonthFilter,
  ]);

  const myCommissions = getMyCommissionData();

  // Build per-month statement summary for this agent (similar to broker Agent Statements section)
  const agentStatements = useMemo(() => {
    type MonthSummary = {
      month: string;
      total: number;
      recordCount: number;
      status: "ready" | "pending" | "no_data";
    };

    const monthMap = new Map<
      string,
      { total: number; recordCount: number; hasIncompleteRecords: boolean }
    >();

    commissions.forEach((record: any) => {
      const share = shareByRecordId.get(Number(record.id));
      if (!share) return;

      const month = record.accountingMonth || record.month;
      if (!month) return;

      const baseCommission = parseCommissionValue(
        record.actualCommission || record.projectedCommission || "0"
      );
      if (baseCommission === 0) return;

      const agentAmount = share.share;

      const isComplete = record.actualPremium && record.actualCommission;
      const existing =
        monthMap.get(month) || {
          total: 0,
          recordCount: 0,
          hasIncompleteRecords: false,
        };

      existing.total += agentAmount;
      existing.recordCount += 1;
      if (!isComplete) existing.hasIncompleteRecords = true;

      monthMap.set(month, existing);
    });

    const summaries: MonthSummary[] = Array.from(monthMap.entries()).map(
      ([month, data]) => ({
        month,
        total: data.total,
        recordCount: data.recordCount,
        status:
          data.recordCount === 0
            ? "no_data"
            : data.hasIncompleteRecords
            ? "pending"
            : "ready",
      })
    );

    // Only keep months with data, sort newest first
    return summaries
      .filter((m) => m.recordCount > 0)
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [commissions, shareByRecordId]);

  // Download PDF statement for the logged-in agent for a specific month
  const handleDownloadAgentStatement = async (month: string) => {
    if (!currentEmployeeIdForStatements) {
      toast({
        title: "No agent found",
        description: "Your employee record could not be determined.",
        variant: "destructive",
      });
      return;
    }

    if (!month) {
      toast({
        title: "Select a month",
        description: "Please choose a statement month.",
        variant: "destructive",
      });
      return;
    }

    try {
      setGeneratingStatementMonth(month);
      const res = await fetch(
        `/api/commission-statements/agent/${currentEmployeeIdForStatements}/${month}`,
        { credentials: "include" }
      );

      if (!res.ok) {
        let message = `Failed to generate statement (${res.status})`;
        try {
          const data = await res.json();
          if (data?.message) message = data.message;
        } catch {
          // ignore JSON parse errors
        }
        toast({ title: "Unable to download", description: message, variant: "destructive" });
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Build a safe filename using available user/employee info
      const firstName: string =
        (employeeRecord && (employeeRecord as any).firstName) ||
        (user && (user as any).firstName) ||
        "agent";
      const lastName: string =
        (employeeRecord && (employeeRecord as any).lastName) ||
        (user && (user as any).lastName) ||
        "";
      const safeName = `${firstName}-${lastName}`.trim();
      const normalized = (safeName || "agent")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();

      a.download = `agent-statement-${normalized}-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        title: "Unexpected error",
        description: error?.message || "An error occurred while downloading your statement.",
        variant: "destructive",
      });
    } finally {
      setGeneratingStatementMonth(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">My Commissions</h2>
      </div>

      {/* Commission Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Commissions</p>
                <p className="text-2xl font-bold" data-testid="my-commission-total">{formatCurrency(analyticsData.totalYTD)}</p>
              </div>
              <DollarSign className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(analyticsData.thisMonthTotal)}
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Assigned Clients</p>
                <p className="text-2xl font-bold" data-testid="my-assigned-clients">{commissionSummary?.assignedClientCount ?? 0}</p>
              </div>
              <Users className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Monthly Growth</p>
                <p className="text-2xl font-bold" data-testid="my-avg-growth">
                  {formatGrowthPct(analyticsData.avgGrowth)}
                </p>
              </div>
              <TrendingUp className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Tracking Tabs */}
      <Tabs defaultValue="commissions" className="space-y-6">
        <TabsList className={tabsListClass}>
          <TabsTrigger value="commissions">My Commissions</TabsTrigger>
          <TabsTrigger value="assignments">Client Assignments</TabsTrigger>
          {canGenerateAgentStatements && (
            <TabsTrigger value="statements">Statements</TabsTrigger>
          )}
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="commissions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Commission Details
              </CardTitle>
              <CardDescription>
                Your commission breakdown by client and carrier
              </CardDescription>
              <div className="flex gap-4 mt-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Client</Label>
                  <Select value={agentCommissionClientFilter} onValueChange={setAgentCommissionClientFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {Array.from(myClientIds).map((clientId: any) => {
                        const clientName = clientNameMap.get(Number(clientId)) || `Client ${clientId}`;
                        return (
                          <SelectItem key={clientId} value={clientId.toString()}>
                            {clientName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Carrier</Label>
                  <Select value={agentCommissionCarrierFilter} onValueChange={setAgentCommissionCarrierFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Carriers</SelectItem>
                      {(() => {
                        const uniqueCarrierIds = Array.from(new Set(myCommissions.map((c: any) => c.carrierId)));
                        return uniqueCarrierIds.map((carrierId: any) => {
                          const carrier = carriers.find((c: any) => c.id === carrierId);
                          return carrier ? (
                            <SelectItem key={carrier.id} value={carrier.id.toString()}>
                              {carrier.name}
                            </SelectItem>
                          ) : null;
                        });
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Month</Label>
                  <Select value={agentCommissionMonthFilter} onValueChange={setAgentCommissionMonthFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {availableMonths.map((month) => {
                        const [year, monthNum] = month.split('-').map(Number);
                        const date = new Date(year, monthNum - 1, 1);
                        return (
                          <SelectItem key={month} value={month}>
                            {date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <button
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => {
                      setAgentCommissionClientFilter("all");
                      setAgentCommissionCarrierFilter("all");
                      setAgentCommissionMonthFilter("all");
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Carrier/Line</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead>Actual Premium</TableHead>
                      <TableHead>Actual Commission</TableHead>
                      <TableHead>Agent Split</TableHead>
                      <TableHead>My Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myCommissions.map((record: any) => {
                      const share = shareByRecordId.get(Number(record.id));
                      const agentAmount = share?.share ?? 0;
                      const hasMultipleAgents = share?.splitPct != null;
                      const isSecondary = !!share?.isSecondary;
                      const agentSplitDisplay = share?.splitPct ?? 100;

                      return (
                        <TableRow key={record.id}>
                          <TableCell className="font-medium">{record.clientName}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{record.carrierName}</p>
                              <p className="text-xs text-muted-foreground">{record.planType}</p>
                            </div>
                          </TableCell>
                          <TableCell>{formatMonth(record.accountingMonth || record.month)}</TableCell>
                          <TableCell>{formatCurrency(record.actualPremium)}</TableCell>
                          <TableCell>{formatCurrency(record.actualCommission)}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{share ? `${share.agentPct}%` : '-'}</p>
                              {hasMultipleAgents && (
                                <p className="text-xs text-muted-foreground">
                                  × {agentSplitDisplay}% ({isSecondary ? 'Secondary' : 'Primary'})
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            {formatCurrency(agentAmount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {myCommissions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    {myClientIds.size === 0 ? (
                      <div>
                        <p className="mb-2">No client assignments found.</p>
                        <p className="text-sm">You need to be assigned as a producer/agent to clients to see commission records.</p>
                      </div>
                    ) : (
                      <div>
                        <p>No commission records found for your assigned clients.</p>
                        <p className="text-sm">Commissions will appear here once they are recorded for your clients.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                My Client Assignments
              </CardTitle>
              <CardDescription>
                Clients you are assigned to as a producer/agent
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Primary/Secondary Split</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const clientGroups: Record<number, any[]> = {};
                      myClientAssignments.forEach((manager: any) => {
                        if (!clientGroups[manager.clientId]) clientGroups[manager.clientId] = [];
                        clientGroups[manager.clientId].push(manager);
                      });

                      return Object.entries(clientGroups).map(([clientIdStr, managers]) => {
                        const clientId = parseInt(clientIdStr);
                        // Get client name from commissions data (clientNameMap) - same as My Commissions tab
                        const clientName = clientNameMap.get(clientId) || `Client ${clientId}`;
                        if (!managers || managers.length === 0) return null;

                        const myAssignment = managers.find((m: any) => currentAccountManagerUserIds.has(String(m.userId)));
                        const role = myAssignment?.role || 'Unknown';
                        const isPrimary = role.toLowerCase().includes('primary');
                        const isSecondary = role.toLowerCase().includes('secondary');

                        // Check if there are multiple agents (primary and secondary producers)
                        const allManagers = allClientAccountManagers.filter((m: any) => m.clientId === clientId);
                        const primaryProducer = allManagers.find((m: any) => 
                          m.role && m.role.toLowerCase().includes('primary') && (m.role.toLowerCase().includes('producer') || m.role.toLowerCase().includes('agent'))
                        );
                        const secondaryProducer = allManagers.find((m: any) => 
                          m.role && m.role.toLowerCase().includes('secondary') && (m.role.toLowerCase().includes('producer') || m.role.toLowerCase().includes('agent'))
                        );
                        const hasMultipleAgents = !!(primaryProducer && secondaryProducer);

                        const split = getClientAgentSplit(clientId);
                        // Show split percentage: if multiple agents, show primary/secondary split; otherwise show agent's portion from agency/agent split
                        let splitDisplay: string;
                        if (hasMultipleAgents) {
                          // Show primary/secondary split
                          splitDisplay = `${split.primaryPercentage}% / ${split.secondaryPercentage}%`;
                        } else {
                          // Single agent gets the full agent portion from agency/agent split
                          const agentSplit = getClientAgencySplit(clientId);
                          splitDisplay = agentSplit.agentPercentage;
                        }

                        return (
                          <TableRow key={clientId}>
                            <TableCell className="font-medium">{clientName}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {isPrimary ? 'Primary Producer' : isSecondary ? 'Secondary Producer' : 'Producer'}
                              </Badge>
                            </TableCell>
                            <TableCell>{splitDisplay}</TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">
                                Active
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      }).filter(Boolean);
                    })()}
                  </TableBody>
                </Table>
                {myClientAssignments.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No client assignments found.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canGenerateAgentStatements && (
        <TabsContent value="statements" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    My Commission Statements (PDF)
                  </CardTitle>
                  <CardDescription>
                    Individual monthly statements based on your commission records and split assignments.
                  </CardDescription>
                </div>
                {agentStatements.length > 0 && (
                  <div className="w-full md:w-64">
                    <Label className="text-sm font-medium">Filter by Month</Label>
                    <Select
                      value={statementFilterMonth}
                      onValueChange={setStatementFilterMonth}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        {agentStatements.map((stmt) => {
                          const [year, monthNum] = stmt.month
                            .split("-")
                            .map(Number);
                          const date = new Date(year, monthNum - 1, 1);
                          const label = date.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                          });
                          return (
                            <SelectItem key={stmt.month} value={stmt.month}>
                              {label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {agentStatements.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-16 w-16 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">
                    No commission data available for statements yet
                  </p>
                  <p className="text-sm">
                    Once commissions are recorded for your clients, monthly statements will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(statementFilterMonth === "all"
                    ? agentStatements
                    : agentStatements.filter(
                        (stmt) => stmt.month === statementFilterMonth
                      )
                  ).map((stmt) => {
                    const [year, monthNum] = stmt.month.split("-").map(Number);
                    const date = new Date(year, monthNum - 1, 1);
                    const monthLabel = date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                    });

                    return (
                      <div
                        key={stmt.month}
                        className="border rounded-lg p-4 bg-muted/30 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {monthLabel
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{monthLabel}</p>
                            <p className="text-sm text-muted-foreground">
                              {stmt.recordCount} commission record
                              {stmt.recordCount !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-2xl font-semibold">
                              {formatCurrency(stmt.total.toFixed(2))}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Commission Earned
                            </p>
                          </div>
                          {stmt.status === "ready" ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">
                              Ready
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200">
                              Data Pending
                            </Badge>
                          )}
                          <Button
                            disabled={
                              stmt.status !== "ready" ||
                              generatingStatementMonth === stmt.month
                            }
                            onClick={() => handleDownloadAgentStatement(stmt.month)}
                          >
                            {generatingStatementMonth === stmt.month
                              ? "Generating…"
                              : "Download PDF"}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="analytics" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Commissions (YTD)</p>
                    <p className="text-2xl font-bold">{formatCurrency(analyticsData.totalYTD)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Avg Monthly Growth</p>
                    <p className="text-2xl font-bold">{formatGrowthPct(analyticsData.avgGrowth)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Top Carrier</p>
                    <p className="text-2xl font-bold">{analyticsData.topCarrier}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Top Coverage</p>
                    <p className="text-2xl font-bold">{analyticsData.topCoverage}</p>
                  </div>
                  <PieChartIcon className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Month-over-Month Variance Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Month-over-Month Commission Trends
              </CardTitle>
              <CardDescription>Track your commission growth and variance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.monthlyTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value: any, name: string) => name === "commissions" ? [`$${value.toLocaleString()}`, "Commission"] : [`${value}%`, "Variance"]} />
                    <Legend />
                    <Line type="monotone" dataKey="commissions" stroke="#8884d8" name="Monthly Commissions" strokeWidth={2} />
                    <Line type="monotone" dataKey="variance" stroke="#82ca9d" name="% Variance" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carrier Breakdown and Coverage Type Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Commission by Carrier
                </CardTitle>
                <CardDescription>Compare commission revenue across carriers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.carrierData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="carrier" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="amount" fill="#8884d8" name="Total Commission" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Commission by Coverage Type
                </CardTitle>
                <CardDescription>Revenue distribution across insurance types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={analyticsData.coverageTypeData} cx="50%" cy="50%" labelLine={false} label={({ name, percentage }) => `${name} ${percentage}%`} outerRadius={100} fill="#8884d8" dataKey="value">
                        <Cell fill="#8884d8" />
                        <Cell fill="#82ca9d" />
                        <Cell fill="#ffc658" />
                        <Cell fill="#ff7c7c" />
                        <Cell fill="#a4de6c" />
                      </Pie>
                      <RechartsTooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

